/**
 * Game is modeled as an array of cards objects (board). Each card is one of N variants.
 * The objective of the game is to open all cards of the same variant in player's turn.
 * 
 * Cards and array of cards are immutable. As the game progresses new array of cards is generated.
 * e.g. to flip a card, new array of cards is created with a card flipped
 * i.e. game state fully represented as an array of cards
 * 
 * Card object has following properties
 * ```
 * {
 *  open: {Boolean},  // card is opened
 *  variant: {Number} // variants are numbered from 0 to N
 * }
 * ```
 * 
 * Set of operation:
 * 
 * Query functions - ask questions about the state
 * - variantRevealed :: [Card], Variant => Boolean 
 * - hasMismatched :: [Card] => Boolean
 * 
 * State transition functions - move from current state to next
 * - dealCards :: void => [Card]
 * - openCard :: [Card], Number => [Card]
 * - closeUnrevealed :: [Card] => [Card]
 */
import * as utils from '../models/utils';

const VARIANTS_DEFAULT = 3; // default number of card variants
const VARIANTS_COPIES = 3; // amount of copies of each variant

export function numberOfVariants(cards) {
  let variants = cards.reduce((v, c) => Math.max(c.variant, v), 0);
  return variants + 1;
}

export function dealCards(numberOfVariants = VARIANTS_DEFAULT) {
  let cards = [];
  for (let v = 0; v < numberOfVariants; v++) 
    for (let j = 0; j < VARIANTS_COPIES; j++)
      cards.push({
        open: false,
        variant: v
      });
  
  utils.shuffleArray(cards);

  return cards;
}

/**
 * Check if a variant is revealed. 
 *  
 * @param {[Card]} cards 
 * @param {Number} variant 
 * @returns Boolean
 */
export function variantRevealed(cards, variant) {
  return cards.filter(card => card.variant === variant).every(card => card.open);
}

/**
 * Open (show the front of) a card
 *  
 * @param {[Card]} cards 
 * @param {Number} i card number 
 * @return {[Card]}
 */
export function openCard(cards, i) {
  // eslint-disable-next-line
  var cards = [...cards]; // discard input, work with copy 
  cards[i].open = true; 

  return cards;
}

/**
 * Number of revealed variants
 *  
 * @param {[Card]} cards 
 * @returns {Number} 
 */
export function revealedVariants(cards) {
  let count = cards.reduce((count, card) => {
    if(!count[card.variant]) 
      count[card.variant] = 0;
    else
      count[card.variant]++;
    return count;
  }, []);

  const variantCopies = cards.length / numberOfVariants(cards);
  return count.filter(n => n === variantCopies).length;
}

/**
 * Close all unrevealed cards
 *  
 * @param {[Cards]} cards 
 * @returns {[Cards]}
 */
export function closeUnrevealed(cards) {
  // eslint-disable-next-line
  var cards = [...cards]; // discard input, work with copy 
  let variants = Array.from({length: cards.length / 2}, (v, i) => i);
  let unrevealedVariants = variants.filter(variant => !variantRevealed(cards, variant));
  cards.forEach(card => {
    card.open = !unrevealedVariants.includes(card.variant);
  });

  return cards;
}

/**
 * Boards has open mismatched cards
 * 
 * @param {[Card]} cards 
 * @returns {Boolean}
 */
export function hasMismatched(cards) {
  // eslint-disable-next-line
  var cards = [...cards]; // discard input, work with copy 

  let variants = Array.from({length: numberOfVariants(cards)}, (v, i) => i);
  let unrevealedVariants = variants.filter(variant => !variantRevealed(cards, variant));
  let openUnrevealed = cards.filter(card => card.open && unrevealedVariants.includes(card.variant));

  return !openUnrevealed.every(card => card.variant === openUnrevealed[0].variant);
}

